import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import type { Object3D, WebGPURenderer } from 'three/webgpu';

import { releaseLoadCache } from '../core/preload';
import { useAppStore } from '../store/appStore';
import { useLoadStore } from '../store/loadStore';

/** Full frames drawn with the whole scene up before the title card is let in. */
const WARM_FRAMES = 3;

/**
 * Longest the warm-up may hold the title card back. A driver that never
 * finishes compiling must cost the user a smooth first second, not the app.
 */
const WARM_TIMEOUT = 15000;

/**
 * After the scene's own per-frame logic, which is what hides the overlays and
 * markers again each frame, and before the display and post passes at 1 and 2
 * draw what is left visible.
 */
const REVEAL_PRIORITY = 0.5;

const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

async function nextFrames(count: number): Promise<void> {
  for (let index = 0; index < count; index += 1) await nextFrame();
}

/** Shows everything under `root` that is hidden, remembering what it touched. */
function revealHidden(root: Object3D, revealed: Set<Object3D>): void {
  root.traverse((object) => {
    if (object.visible) return;
    object.visible = true;
    revealed.add(object);
  });
}

function hideAgain(revealed: Set<Object3D>): void {
  for (const object of revealed) object.visible = false;
  revealed.clear();
}

/**
 * Builds every GPU pipeline the instrument needs while the loader is still up.
 *
 * Mounted inside the suspense boundary, so it runs once the models are parsed
 * and in the scene. Two passes, because neither is sufficient alone:
 * `compileAsync` builds the programs for everything in the scene without
 * drawing, and then a handful of real frames go through the post chain, which
 * is what proves the pipelines the renderer actually draws with — the scene
 * pass renders to multiple targets, so those differ from what `compileAsync`
 * builds against the canvas.
 *
 * Both passes cover the parts that are hidden at this point, the lit overlays
 * and state markers above all, which the renderer would otherwise skip and
 * compile mid-reveal instead. The scene re-hides them every frame, so for the
 * real frames they are forced back on after that logic has run.
 *
 * All of it happens behind an opaque loader with the instrument burnt down to
 * nothing, so the frames it costs are ones nobody sees.
 */
export function Warmup() {
  const renderer = useThree((state) => state.gl) as unknown as WebGPURenderer;
  const scene = useThree((state) => state.scene);
  const camera = useThree((state) => state.camera);
  const ready = useAppStore((state) => state.ready);

  const drawingWarmFrames = useRef(false);
  const revealed = useRef(new Set<Object3D>());

  useFrame(() => {
    if (drawingWarmFrames.current) revealHidden(scene, revealed.current);
  }, REVEAL_PRIORITY);

  useEffect(() => {
    let cancelled = false;
    const { setStep } = useLoadStore.getState();

    // Reaching here at all means the models are parsed and mounted.
    setStep('scene', 1);

    const compile = async () => {
      revealHidden(scene, revealed.current);
      try {
        await renderer.compileAsync(scene, camera);
      } finally {
        // `compileAsync` builds pipelines without drawing, so nothing was ever
        // on screen to put back.
        hideAgain(revealed.current);
      }
    };

    const draw = async () => {
      drawingWarmFrames.current = true;
      try {
        await nextFrames(WARM_FRAMES);
      } finally {
        drawingWarmFrames.current = false;
        hideAgain(revealed.current);
      }
    };

    const warm = async () => {
      // The scene is populated during the commit this effect belongs to; a
      // frame's grace lets R3F finish placing everything before it is compiled.
      await nextFrame();
      if (cancelled) return;

      // Everything the loader fetched is now a scene graph and a set of GPU
      // textures, so the files themselves are just weight.
      releaseLoadCache();

      await compile();
      if (cancelled) return;
      setStep('shaders', 0.7);

      await draw();
      setStep('shaders', 1);
    };

    let expiry: ReturnType<typeof setTimeout>;
    const expire = new Promise<void>((resolve) => {
      expiry = setTimeout(resolve, WARM_TIMEOUT);
    });

    void Promise.race([warm().catch(() => {}), expire]).then(() => {
      if (!cancelled) ready();
    });

    return () => {
      cancelled = true;
      clearTimeout(expiry);
      drawingWarmFrames.current = false;
      hideAgain(revealed.current);
    };
  }, [renderer, scene, camera, ready]);

  return null;
}
